import { events } from "@constants/tracking";
import CustomKeyboardAvoidingView from "@features/account/components/CustomKeyboardAvoidingView";
import { isWeb } from "@helpers/platform";
import { useAppTheme } from "@hooks/useAppTheme";
import { useDevice } from "@hooks/useDevice";
import type {
	BaseItemSchema,
	OriginalItemTypes,
	RenderableItemTypes,
} from "@memorang/types/src/session/new-session";
import { Box, MarkdownRenderer } from "@memorang/ui";
import { trackCustomEvent } from "analytics";
import { useEffect, useRef } from "react";
import { Dimensions, ScrollView } from "react-native";
import { sessionMaxWidth } from "../constants";
import InteractiveVideoContainer from "../containers/InteractiveVideoContainer";
import MarkingAnswerContainer from "../containers/MarkingAnswerContainer";
import StudySessionFeedbackContainer from "../containers/StudySessionFeedbackContainer";
import {
	getIsSlidesHasPassage,
	getSlidesData,
	isAiSimulationItem,
} from "../helpers";
import composeSessionMd from "../helpers/composeSessionMd";
import useSessionStore from "../hooks/useSessionStore";
import PassageStemContainer from "./PassageStemContainer";
import { StemContainer } from "./StemContainer";

interface Props {
	children?: React.ReactNode;
	item: BaseItemSchema;
	toggleShowReadMore?: () => void;
	originalItemType: OriginalItemTypes;
	mappedItemType: RenderableItemTypes;
	handleHighlightedTextPress?: () => void;
	footer?: React.ReactNode;
	instructions?: string;
	showInstructions?: boolean;
	selectedChoiceIds?: string[];
	onFillInBlankChange?: (index: number, value: string) => void;
	fillInBlankValue?: string;
	fillInBlankValues?: string[];
	isFillInTheBlanks?: boolean;
	leftPanel?: React.ReactNode;
	suppressPassageLayout?: boolean;
}

const screenWidth = Dimensions.get("window").width;
const ItemWrapper = ({
	children,
	item,
	toggleShowReadMore,
	originalItemType,
	mappedItemType,
	handleHighlightedTextPress,
	footer,
	instructions,
	showInstructions,
	selectedChoiceIds,
	onFillInBlankChange,
	fillInBlankValue,
	fillInBlankValues,
	isFillInTheBlanks,
	leftPanel,
	suppressPassageLayout,
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
}: Props) => {
	const { id, content, stimulus: parentStimulus } = item;

	const theme = useAppTheme();

	const parentStimulusType = parentStimulus?.metadata.type;
	const parentTitle =
		parentStimulus?.content.passage?.title || parentStimulus?.content.className;
	const currentStimulusItemIndex = item.currentStimulusItemIndex;
	const numTotalStimulusItems = item.numTotalStimulusItems;
	const parentInstruction = parentStimulus?.content.instruction;
	const parentSlides = parentStimulus?.content.slides;
	const parentProfessor = parentStimulus?.content.professor;

	const parentVideo = parentStimulus?.content.media?.find(
		(media) => media.type === "video" || media.type === "VIDEO",
	)?.url;

	const skill = parentStimulus?.metadata.skill;

	const slideshowData = getSlidesData(parentSlides);

	const media = content?.media;
	const stem = composeSessionMd({
		content: content?.stem,
		highlights: content?.highlights,
		insertText: item.content?.insertSentence,
		isStem: true,
	});

	const audio = media?.find(
		(media) => media.type === "audio" || media.type === "AUDIO",
	)?.url;
	const isScenario = mappedItemType === "SCENARIO";
	const video = isScenario
		? undefined
		: media?.find((media) => media.type === "video" || media.type === "VIDEO")
				?.url;
	const image = media?.find(
		(media) => media.type === "image" || media.type === "IMAGE",
	)?.url;

	const isSlidesHasPassage = getIsSlidesHasPassage(parentSlides);

	const parentAudio = parentStimulus?.content.media?.find(
		(media) => media.type === "audio" || media.type === "AUDIO",
	)?.url;

	const parentImage = parentStimulus?.content.media?.find(
		(media) => media.type === "image" || media.type === "IMAGE",
	)?.url;

	const parentStem = composeSessionMd({
		content: item.stimulus?.content.passage,
		highlights: item.content?.highlights,
		insertText: item.content?.insertSentence,
		paragraphNumber: item.content?.paragraphNumber,
		choices: item.content?.choices,
		selectedChoiceIds,
		shouldFormat: isWeb,
	});

	const itemStates = useSessionStore((state) => state.itemStates);
	const itemState = itemStates?.get(id!);
	const isAnswered = itemState?.answered;

	const updateHighlightedItems = useSessionStore(
		(state) => state.updateHighlightedItems,
	);
	const { isMobile, isMediumWindowSize } = useDevice();

	const isSummativeTest = useSessionStore((state) => state.isSummativeTest);
	const isProethicaTest = useSessionStore(
		(state) => state.proethicaTestKind != null,
	);

	const updateItemStates = useSessionStore((state) => state.updateItemStates);

	const hasParentStem =
		((parentStem && parentStem.length > 0) ||
			(parentTitle && parentTitle.length > 0)) &&
		isWeb &&
		!isMobile &&
		!isMediumWindowSize &&
		!suppressPassageLayout;

	const handleStoreHtml = (html: string) => {
		updateHighlightedItems(id!, html);
	};

	const handleSelectChoice = (choiceId: string, crossingOut?: boolean) => {
		trackCustomEvent({
			eventName: crossingOut
				? events.sessionItemCrossedOut
				: events.sessionItemOptionSelected,
			choiceId,
			itemId: id!,
		});
		updateItemStates({
			itemId: id!,
			selectedChoiceId: choiceId,
			multiSelect: mappedItemType === "SATA",
			crossOut: crossingOut,
		});
	};

	const handleClickInsertableText = (choiceId: string) => {
		trackCustomEvent({
			eventName: events.sessionItemTextInserted,
			choiceId,
			itemId: id!,
		});
		handleSelectChoice(choiceId);
	};

	const showTTS = parentStimulusType === "LISTENING";

	const isMarkingAnswer = mappedItemType === "MARKING_ANSWER";

	const isInteractiveVideo = mappedItemType === "INTERACTIVE_VIDEO";

	const customKeyboardAvoidingViewRef = useRef<ScrollView | null>(null);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (customKeyboardAvoidingViewRef.current) {
			customKeyboardAvoidingViewRef.current.scrollTo({ y: 0, animated: false });
		}
	}, [id]);
	const hasPassage = !suppressPassageLayout && (parentStem || parentTitle);

	const isExploreAccordion = mappedItemType === "EXPLORE_ACCORDION";
	const shouldCenterContent = isWeb && !hasParentStem;

	const isAiSimulation =
		mappedItemType === "SCENARIO" && isAiSimulationItem(item);

	return (
		<Box
			gap={16}
			flexGrow={1}
			height="100%"
			paddingHorizontal={
				isWeb && isSummativeTest && !isProethicaTest && !isMobile ? 24 : 0
			}
			width={isWeb ? "auto" : screenWidth}
			justifyContent="space-between"
			paddingBottom={64}
		>
			<Box flex={1} flexDirection={hasParentStem ? "row" : "column"}>
				{hasPassage ? (
					<Box
						flex={hasParentStem ? 1 : undefined}
						height={hasParentStem ? "100%" : undefined}
						paddingBottom={54}
					>
						{leftPanel ? (
							<ScrollView showsVerticalScrollIndicator={false}>
								<Box
									paddingRight={isWeb ? 16 : 0}
									height="100%"
									gap={16}
									paddingBottom={100}
									style={{ overflowY: "auto" }}
								>
									{leftPanel}
								</Box>
							</ScrollView>
						) : (
							<PassageStemContainer
								stem={parentStem || ""}
								video={parentVideo}
								audio={parentAudio}
								toggleShowReadMore={toggleShowReadMore}
								id={id}
								index={currentStimulusItemIndex}
								parentType={parentStimulusType!}
								handleStoreHtml={handleStoreHtml}
								parentVideo={parentVideo}
								parentImage={parentImage}
								handleClickInsertableText={handleClickInsertableText}
								isSummativeTest={isSummativeTest}
								numPassageItems={numTotalStimulusItems}
								parentTitle={parentTitle}
								slideshowData={slideshowData}
								parentInstruction={parentInstruction}
								professor={parentProfessor}
								slides={parentSlides}
								isSlidesHasPassage={isSlidesHasPassage}
								skill={skill!}
								disableInsertableText={isAnswered}
							/>
						)}
					</Box>
				) : null}
				{hasPassage && !isMobile && !isMediumWindowSize && (
					<Box
						width={1}
						height={"100%"}
						backgroundColor={theme.colors.outlineVariant}
						marginHorizontal={16}
					/>
				)}
				<CustomKeyboardAvoidingView
					showsVerticalScrollIndicator={false}
					style={{
						flex: 1,
					}}
					contentContainerStyle={{
						flexGrow: 1,
						paddingBottom: isAnswered && !isWeb ? 300 : 64,
						paddingTop: isSummativeTest ? 16 : 0,
						gap: 16,
					}}
					ref={customKeyboardAvoidingViewRef}
				>
					<Box
						gap={16}
						maxWidth={isAiSimulation ? undefined : sessionMaxWidth.formative}
						alignSelf={shouldCenterContent ? "center" : undefined}
						width={
							shouldCenterContent || isMediumWindowSize ? "100%" : undefined
						}
						flex={1}
					>
						{!isExploreAccordion && (
							<StemContainer
								stem={stem || ""}
								instructions={instructions}
								showInstructions={showInstructions}
								video={video}
								audio={audio}
								key={id}
								handleStoreHtml={handleStoreHtml}
								isSummativeTest={isSummativeTest}
								itemId={id!}
								isAnswered={isAnswered}
								showTTS={showTTS}
								handleHighlightedTextPress={handleHighlightedTextPress}
								originalItemType={originalItemType}
								onFillInBlankChange={onFillInBlankChange}
								fillInBlankValue={fillInBlankValue}
								fillInBlankValues={fillInBlankValues}
								isFillInTheBlanks={isFillInTheBlanks}
							/>
						)}
						<Box gap={8}>
							{showInstructions && instructions && (
								<Box paddingHorizontal={isMobile ? 16 : 0}>
									<MarkdownRenderer
										text={instructions}
										fontSize={16}
										paragraphMarginVertical={2}
									/>
								</Box>
							)}
							{isInteractiveVideo && isWeb && (
								<InteractiveVideoContainer
									itemId={id!}
									isAnswered={isAnswered}
								/>
							)}
							{isMarkingAnswer && image && isWeb && (
								<MarkingAnswerContainer
									image={image}
									itemId={id!}
									isAnswered={isAnswered}
								/>
							)}

							{children}
						</Box>
						{isWeb && (
							<StudySessionFeedbackContainer
								hasParentStem={Boolean(hasParentStem)}
							/>
						)}
					</Box>
				</CustomKeyboardAvoidingView>
			</Box>
			{footer}
		</Box>
	);
};

export default ItemWrapper;
